import { useCallback, useMemo } from 'react';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { SUGGESTED_DARE_PROMPT_CONFIG, } from 'root/src/shared/constants/suggestedDarePrompt';
import { isDropDismissed, isPromptDue, } from 'root/src/client/v2/creator/components/SuggestedDareModal/cadence';
import { getLocalLastShownAt, getLocalSnoozeUntil, markPromptShownLocally, markPromptSnoozedLocally, } from 'root/src/client/v2/creator/components/SuggestedDareModal/promptCadenceLocalGuard';
const HOUR_MS = 60 * 60 * 1000;
const FALLBACK_CONFIG = {
    enabled: SUGGESTED_DARE_PROMPT_CONFIG.enabled,
    minIntervalHours: SUGGESTED_DARE_PROMPT_CONFIG.minIntervalHours,
    snoozeHours: SUGGESTED_DARE_PROMPT_CONFIG.snoozeHours,
    dismissDropDays: SUGGESTED_DARE_PROMPT_CONFIG.dismissDropDays,
    candidatePoolSize: SUGGESTED_DARE_PROMPT_CONFIG.candidatePoolSize,
    minSimilarityScore: SUGGESTED_DARE_PROMPT_CONFIG.minSimilarityScore,
    similarityRatio: SUGGESTED_DARE_PROMPT_CONFIG.similarityRatio,
    showDelaySeconds: SUGGESTED_DARE_PROMPT_CONFIG.showDelaySeconds,
};
export const useSuggestedDarePromptCadence = (options) => {
    var _a, _b;
    const enabled = (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true;
    const utils = trpc.useUtils();
    const stateQuery = trpc.drops.getCreatorPromptState.useQuery(undefined, {
        enabled: SUGGESTED_DARE_PROMPT_CONFIG.enabled && enabled,
        staleTime: 60000,
    });
    const shownMutation = trpc.drops.recordCreatorPromptShown.useMutation({
        onSuccess: () => utils.drops.getCreatorPromptState.invalidate(),
    });
    const snoozeMutation = trpc.drops.recordCreatorPromptSnooze.useMutation({
        onSuccess: () => utils.drops.getCreatorPromptState.invalidate(),
    });
    const dismissMutation = trpc.drops.recordCreatorPromptDismiss.useMutation({
        onSuccess: () => utils.drops.getCreatorPromptState.invalidate(),
    });
    const acceptedMutation = trpc.drops.recordCreatorPromptAccepted.useMutation();
    const state = stateQuery.data;
    const activeConfig = (_b = state === null || state === void 0 ? void 0 : state.config) !== null && _b !== void 0 ? _b : FALLBACK_CONFIG;
    const isReady = enabled
        ? Boolean(stateQuery.data) && !stateQuery.isLoading
        : false;
    const isDue = useMemo(() => {
        var _a, _b, _c, _d;
        if (!state || !activeConfig.enabled) {
            return false;
        }
        // Merge server cadence with the local optimistic guard so a remount/reload before the server
        // state refetches still sees the most recent show/snooze and won't re-show the prompt.
        const lastShownAt = Math.max((_a = state.lastShownAt) !== null && _a !== void 0 ? _a : 0, (_b = getLocalLastShownAt()) !== null && _b !== void 0 ? _b : 0) || null;
        const snoozeUntil = Math.max((_c = state.snoozeUntil) !== null && _c !== void 0 ? _c : 0, (_d = getLocalSnoozeUntil()) !== null && _d !== void 0 ? _d : 0) || null;
        return isPromptDue({
            lastShownAt,
            snoozeUntil,
            dismissedDropIds: state.dismissedDropIds,
        }, Date.now(), activeConfig);
    }, [state, activeConfig]);
    const isDropCurrentlyDismissed = useCallback((dropId) => {
        if (!state) {
            return false;
        }
        return isDropDismissed(state.dismissedDropIds, dropId);
    }, [state]);
    const recordShown = useCallback((context) => {
        // Optimistically mark locally before the server round-trip closes the cadence gate.
        markPromptShownLocally(Date.now());
        shownMutation.mutate(context);
    }, [shownMutation]);
    const recordSnooze = useCallback((context) => {
        markPromptSnoozedLocally(Date.now() + activeConfig.snoozeHours * HOUR_MS);
        snoozeMutation.mutate(context);
    }, [snoozeMutation, activeConfig.snoozeHours]);
    const recordDismiss = useCallback((dropId, context) => {
        dismissMutation.mutate(Object.assign({ dropId }, context));
    }, [dismissMutation]);
    const recordAccepted = useCallback((context) => {
        acceptedMutation.mutate(context);
    }, [acceptedMutation]);
    return {
        isReady,
        isDue,
        activeConfig,
        isDropCurrentlyDismissed,
        recordShown,
        recordSnooze,
        recordDismiss,
        recordAccepted,
    };
};
